import { Component, computed, effect, inject, linkedSignal, signal, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessStore } from 'src/model/business/business-store';
import { Customer } from 'src/model/customer.model';
import { EINVOICE_START_YEAR, EInvoiceDoc } from 'src/model/e-invoice..model';
import { FileInterface } from 'src/model/file.interface';
import { FormJ } from 'src/model/formJ.model';
import { EInvoiceService } from 'src/services/e-invoice.service';
import { FileService } from 'src/services/file.service';
import { FiscalYearInfoService } from 'src/services/fiscal-year-info.service';
import { FormJService } from 'src/services/form-j.service';
import { ConstructAPI } from '../API/constructAPI';
import { ALERT_TYPE, ERROR_MESSAGES, FORMJ_SCHEME, INJECT_TYPE } from '../constants';
import { DialogItem } from '../dialog-item';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { ThumbnailsListComponent } from '../Images/thumbnails-list/thumbnails-list.component';
import { MatTableController } from '../mat-table-controller';
import { constructAbsoluteMonths, fromAbsoluteMonth, isWithinAbsoluteMonthRange, toAbsoluteMonth } from '../month.util';
import { SharedService } from '../Shared/shared-service.service';
import { toFiscalYearInfoAbsMonths } from 'src/model/fiscalYearInfo.model';

export enum Status {
  Selected = 0,
  Submitted = 1
}

interface FormJSelection {
  formJ: FormJ;
  monthlyInterest: number;
  months: [number[], number[]];
  annualInterest?: number;
  cfTerm: number;
  cfAmount: number;
  // lastTerm: Date;
  absStartMonth: number;
  absEndMonth: number;
  stampedFormJ?: FileInterface[];
  show?: boolean;
  cancelled?: boolean;
}

interface FormJSelectionGroup {
  group: number;
  selected: boolean;
  selectionList: FormJSelection[];
  selectableCount: number;
  selectedCount: number;
  selectionListCount: number;
  expanded?: boolean;
}

@Component({
  selector: 'app-form-j-selection',
  templateUrl: './form-j-selection.component.html',
  styleUrl: './form-j-selection.component.scss',
  providers: [MatTableController],
  standalone: false
})
export class FormJSelectionComponent {
  // private businessService = inject(BusinessService);
  private businessStore = inject(BusinessStore);
  private formJService = inject(FormJService);
  private eInvoiceService = inject(EInvoiceService);
  private fileService = inject(FileService);
  private fyInfoService = inject(FiscalYearInfoService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private constructApi = inject(ConstructAPI);
  protected sharedService = inject(SharedService);
  protected table = inject(MatTableController<FormJSelection>);
  protected Status = Status;

  isWithinAbsoluteMonthRange = isWithinAbsoluteMonthRange;

  businessList = this.businessStore.businessList;
  businessMap = this.businessStore.businessMap;
  branchBusinessMap = this.businessStore.branchBusinessMap;
  businessId = signal<number>(undefined);
  branchId = signal<number>(undefined);
  formJList = signal<FormJ[]>([]);
  newEInvoiceDoc = new EInvoiceDoc();
  customerList: Customer[] = [];

  limit = signal<number>(undefined);
  accumNextYearCF = signal<number>(undefined);
  prevYearBF = signal<number>(undefined);

  readonly latestDay: number = 7;
  readonly today = new Date();
  currentYear = signal<number>(this.today.getFullYear());
  currentMonth = signal<number>(this.today.getDate() > this.latestDay ? this.today.getMonth() : this.today.getMonth() - 1);
  readonly years = Array.from({ length: this.currentYear() + 2 - EINVOICE_START_YEAR + 1 }, (_, i) => EINVOICE_START_YEAR -1 + i).sort((a, b) => b - a);
  absCurrentMonth = computed(() => toAbsoluteMonth(this.currentYear(), this.currentMonth()));
  absStartMonth = computed(() => this.absMonths()[0].id);
  absEndMonth = computed(() => this.absMonths()[this.absMonths().length - 1].id);
  currentFY = computed<number>(() => this.currentMonth() > this.yearEnd() ? this.currentYear() + 1 : this.currentYear());
  // fyStartDate = computed(() => new Date(this.currentFY() - 1, this.yearEnd() + 1, 1));
  yearEnd = signal<number>(-1);
  monthsLong: string[] = Array.from({ length: 12 }, (_, i) => i).map(i => this.sharedService.formatMonth(i, 'long'));
  absMonths = computed(() => constructAbsoluteMonths(new Date(this.currentYear(), this.currentMonth(), 1), this.yearEnd()));

  allEInvoiceDocs = signal<EInvoiceDoc[]>([]);
  docs = computed(() => {
    return this.allEInvoiceDocs().reduce((acc, item) => {
      const absMonth = toAbsoluteMonth(item.IssuanceDate.getFullYear(), this.getDocMonth(item.IssuanceDate));
      if (absMonth > this.absCurrentMonth()) return acc;
      const current = absMonth === this.absCurrentMonth();
      const submitted = item.SubmissionUuid !== undefined;
      if (submitted) {
        acc[current ? 'currentSubmitted' : 'prevSubmitted'].push(item);
      } else {
        const key = current ? 'currentDraft' : 'prevDraft';
        const t = item.CreatedOn.getTime();
        const latestTime = acc[key][0]?.CreatedOn.getTime() ?? -Infinity;
        acc[key] = t > latestTime ? [item] : t === latestTime ? [...acc[key], item] : acc[key];
      }
      return acc;
    }, {prevDraft: [] as EInvoiceDoc[], currentDraft: [] as EInvoiceDoc[], prevSubmitted: [] as EInvoiceDoc[], currentSubmitted: [] as EInvoiceDoc[]});
  });
  submitted = computed(() =>  this.docs().currentSubmitted.length > 0);
  submittedFormJ = computed(() => {
    const p = this.docs().prevSubmitted;
    const s = this.docs().currentSubmitted;
    return [...p, ...s].reduce((acc, item) => {
      const month = toAbsoluteMonth(item.IssuanceDate.getFullYear(), this.getDocMonth(item.IssuanceDate));
      // if (month >= this.absStartMonth() && month <= this.absEndMonth())
        item.FormJIds.forEach(f => (acc[f] ??= new Set()).add(month));
      return acc;
    }, {} as Record<number, Set<number>>); // [formJId]: [epochMonths]
  });
  draftedFormJ = computed(() => {
    return this.docs().prevDraft.reduce((acc, item) => {
      const month = toAbsoluteMonth(item.IssuanceDate.getFullYear(), this.getDocMonth(item.IssuanceDate));
      // if (month >= this.absStartMonth() && month <= this.absEndMonth())
        item.FormJIds.forEach(f => (acc[f] ??= new Set()).add(month));
      return acc;
    }, {} as Record<number, Set<number>>); // [formJId]: [absMonths]
  });

  minIssuanceDate = computed(() => new Date(this.currentYear(), this.currentMonth(), this.latestDay + 1));
  maxIssuanceDate = computed(() => this.sharedService.getLastMsOfDate(new Date(this.currentYear(), this.currentMonth() + 1, this.latestDay)));
  minDate = computed(() => {
    // * Issuance Date must not be older than 72 hours, 2 hours buffer time for submission
    const min = new Date();
    min.setHours(min.getHours() - 70);
    return new Date(Math.max(min.getTime(), this.minIssuanceDate().getTime()));
  });
  maxDate = computed(() => new Date(Math.max(new Date().getTime(), this.maxIssuanceDate().getTime())));
  originalIssuanceDate = computed<Date>(() => this.docs().currentDraft[0]?.IssuanceDate ?? new Date());
  issuanceDate = linkedSignal<Date>(() => this.docs().currentDraft[0]?.IssuanceDate ?? this.docs().currentSubmitted[0]?.IssuanceDate ?? this.maxDate());

  currentMonthAmount = computed(() => this.formJSelection().reduce((total, j) => total +  + (j.months.flat().includes(this.absCurrentMonth()) ? j.monthlyInterest : 0), 0));
  projectedAmount = computed(() => this.formJSelection().reduce((total, j) => total + this.calcFYTotalAmount(j), this.prevYearBF()));
  currentAmount = computed(() => this.formJSelection().reduce((total, j) => total + j.monthlyInterest * j.months.flat().filter(n => n <= this.absCurrentMonth()).length, 0));
  nextYearCF = computed(() => this.formJSelection().reduce((total, j) => total + (j.months.flat().includes(this.absCurrentMonth()) ? Math.min(12, j.cfTerm) * j.monthlyInterest : 0), 0) + (this.accumNextYearCF() ?? 0));

  expansionState = signal<[boolean, boolean, boolean]>([false, true, false]); // 0 - Previously Selected, 1 - Current Month, 2 - Not Selected
  formJSelection = signal<FormJSelection[]>([]);
  group = computed<FormJSelectionGroup[]>(() => {
    const f = this.formJSelection();
    const selectionMap = f.reduce((map, j) => {
      const selected = j.months.flat().some(m => m < this.absCurrentMonth()) ? true : (j.formJ.DateStamped >= new Date(this.currentYear(), this.currentMonth() - 1, 1) && j.formJ.DateStamped < new Date(this.currentYear(), this.currentMonth(), 1) ? undefined : false);
      const i = selected === false ? 2 : selected === true ? 0 : 1;
      if (!map.has(i)) map.set(i, { group: i, selected: selected, selectionList: [], selectableCount: 0, selectedCount: 0, selectionListCount: 0 });
      map.get(i)!.selectionList.push(j);
      return map;
    }, new Map<number, FormJSelectionGroup>());
    return Array.from(selectionMap.values()).sort((a, b) => a.group - b.group);
  });
  formJSelectionGroup = computed(() => {
    const data = this.group();
    const state = this.expansionState();
    return data.map(g => (
      { ...g, expanded: state[g.group], selectionList: g.selectionList.map(j => ({ ...j, show: state[g.group] })), selectableCount: g.selectionList.filter(j => j.absEndMonth >= this.absCurrentMonth()).length, selectedCount: g.selectionList.filter(j => j.months.flat().some(m => m === this.absCurrentMonth())).length, selectionListCount: g.selectionList.length }));
  })

  displayedColumns = computed(() => {
    const cols = [...this.table.columnDefs()];
    const i = cols.indexOf('FormJDate');
    if (i > -1) cols.splice(i+1, 0, 'File');
    const j = cols.indexOf('MonthlyInterest');
    if (j > -1) cols.splice(j+1, 0, ...this.absMonths().map(m => m.name));
    return cols;
  });

  groupHeaderColumns = computed(() => ['groupHeader', 'groupCurrentAmount', ...this.absMonths().map(m => m.name), 'groupTotal', 'groupCF']);
  // currentDocs = signal<EInvoiceDoc[]>([]);
  // submittedDocs = signal<EInvoiceDoc[]>([]);
  // prevSubmittedDoc = signal<EInvoiceDoc[]>([]);
  // prevDraftDoc = signal<EInvoiceDoc[]>([]);

  currentSelected = signal<number[]>([]);
  // prevSubmitted = signal<Record<number, number[]>>({});
  selectionChanged = computed(() => {
    const a = this.currentSelected() ?? [];
    const b = this.docs().currentDraft.flatMap(e => e.FormJIds);
    return (a.length !== b.length || [...new Set(a)].some(v => !new Set(b).has(v)));
  });
  dateChanged = computed(() => this.issuanceDate().getTime() !== this.originalIssuanceDate().getTime());

  formJDetailsTemplate = viewChild.required<TemplateRef<any>>('formJDetails');

  constructor() {
    const query = this.route.snapshot.queryParamMap;
    const b = query.get('businessId');
    if (b && Number(b)) this.businessId.set(Number(b));
    const r = query.get('branchId');
    if (r && Number(r)) this.branchId.set(Number(r));
    const y = query.get('currentYear');
    if (y && Number(y)) this.branchId.set(Number(y));
    const c = query.get('branchId');
    if (c && Number(c)) this.branchId.set(Number(c));

    this.table.config.set([
      // { columnDef: 'position', header: '#', type: 'index' as const },
      { columnDef: 'Name', header: 'Name', property: 'formJ.CustomerName', onClick: (row) => this.goToCustomer(row.formJ.CustomerId) },
      { columnDef: 'ICNumber', header: 'IC No.', property: 'formJ.IcNumber' },
      { columnDef: 'FormJDate', header: 'Date', property: 'formJ.DateCreated', type: 'date' },
      // { columnDef: 'FormJId', header: 'Form J ID', property: 'formJ.Id' },
      { columnDef: 'Term', header: 'Term', property: 'formJ.Term', cssClass: { header: 'align-end', cell: 'text-end' } },
      { columnDef: 'Amount', header: 'Amount', property: 'formJ.Principal', cssClass: { header: 'align-end', cell: 'text-end' } },
      { columnDef: 'MonthlyInterest', header: 'Monthly Int.', property: 'monthlyInterest', type: 'currency', footer: (data) => data.reduce((total, j) => total + (j.months.flat().includes(this.absCurrentMonth()) ? j.monthlyInterest : 0), 0), cssClass: { header: 'align-end', cell: 'text-end', footer: 'text-end fw-bold' } },
      { columnDef: 'Total', header: 'Total', calculated: (j) => this.calcFYTotalAmount(j), footer: (data) => data.reduce((total, j) => total + this.calcFYTotalAmount(j), 0), type: 'currency', cssClass: { header: 'align-end', cell: 'text-end', footer: 'text-end fw-bold' } },
      { columnDef: 'CFTerm', header: 'CF Term', property: 'cfTerm', cssClass: { header: 'align-end', cell: 'text-end' } },
      { columnDef: 'CFAmount', header: 'CF', property: 'cfAmount', footer: (data) => data.reduce((total, l) => total + (l.months.flat().includes(this.absCurrentMonth()) ? l.cfAmount : 0), 0), type: 'currency', cssClass: { header: 'align-end', cell: 'text-end', footer: 'text-end fw-bold' } },
    ]);

    effect(() => {
      const businessId = this.businessId();
      const branchId = this.branchId();
      const currentYear = this.currentYear();
      const currentMonth = this.currentMonth();
      if (businessId !== undefined && branchId !== undefined) {
        this.setRoute(businessId, branchId, currentYear, currentMonth);
        this.currentSelected.set([]);
        this.allEInvoiceDocs.set([]);
        this.getFiscalYearInfo(branchId);
      }
    })

    effect(() => {
      const businessId = this.businessId();
      if (this.businessMap().get(businessId))
        this.yearEnd.set(this.businessMap().get(businessId).YearEndMonth!);
    })

    effect(() => {
      const limit = this.limit();
      if (limit !== undefined) {
        const branchId = this.branchId();
        const currentMonth = this.currentMonth();
        this.getPrevFormJs(branchId, currentMonth);
        this.getEInvoiceDocs(branchId);
      }
    })

    effect(() => {
      const formJList = this.formJList();
      const docs = this.docs();
      this.constructFormJSelection(formJList, docs);
    })

    effect(() => {
      const group = this.formJSelectionGroup();
      this.table.rawData.set([...group.map(f => ({ headerRow: f, items: f.selectionList }))]);
    })

    effect(() => {
      if (!this.submitted() && this.allEInvoiceDocs().length > 0) {
        this.checkPrevMonthSubmitted();
      }
    })
  }

  calcFYTotalAmount(row: FormJSelection) {
    const months = row.months.flat().filter(m => m >= this.absStartMonth() && m <= this.absEndMonth());
    return row.monthlyInterest * (months ? months.length : 0);
  }

  setRoute(businessId: number, branchId: number, currentYear: number, currentMonth: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        businessId: businessId,
        branchId: branchId,
        currentYear: currentYear,
        currentMonth: currentMonth
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  async getFiscalYearInfo(branchId: number) {
    const list = await this.fyInfoService.getFiscalYearInfos({branchId});
    list.sort((a, b) => b.FiscalYear - a.FiscalYear);
    const yearEnd = this.branchBusinessMap().get(branchId)?.YearEndMonth;
    const fyInfoList = list.map(f => toFiscalYearInfoAbsMonths(f, yearEnd));
    const i = fyInfoList.findIndex(y => this.absCurrentMonth() <= y.AbsEndMonth);
    if (list.length === 0 || i === -1) {
      this.sharedService.openAlert('Please add fiscal year info before select Form J for e-invoice.', ALERT_TYPE.DANGER);
      this.reset();
      return;
    }
    this.limit.set(list[i].Amount);
    this.accumNextYearCF.set(list[i].CfAdjustment);
    this.prevYearBF.set(list[i+1]?.CfAdjustment ?? 0);
  }

  async getEInvoiceDocs(businessId: number) {
    const docs = await this.eInvoiceService.getAllEInvoiceDocs({ businessId: businessId, issuedBefore: this.maxIssuanceDate() });
    this.allEInvoiceDocs.set(docs ?? []);
  }

  async getPrevFormJs(businessId: number, currentMonth: number) {
    const res = await this.formJService.getAllFormJs({ businessId: businessId, stamped: true, scheme: FORMJ_SCHEME.EPF, hasLoanGroup: true, stampedBefore: new Date(this.currentYear(), currentMonth, 1) });
    this.formJList.set(res);
  }

  constructFormJSelection(formJList: FormJ[], docs: {currentDraft: EInvoiceDoc[], currentSubmitted: EInvoiceDoc[], prevSubmitted: EInvoiceDoc[], prevDraft: EInvoiceDoc[]}) {
    const absCurrent = this.absCurrentMonth();
    const draft = docs.currentDraft?.flatMap(e => e.FormJIds);
    const draftSet = new Set(draft);
    const currentSelected: number[] = [];
    const formJSelection = formJList.reduce((acc, item) => {
      const start = toAbsoluteMonth(item.DateStamped);
      const end = start + item.Term;
      const cfTerm =  Math.max(0, end - this.absEndMonth());
      const monthlyInterest = item.Principal * 0.015;
      if (end >= this.absStartMonth()) {
        // #region 'Selection'
        const submitted: Set<number> = new Set([...this.submittedFormJ()[item.Id] ?? [], ...this.draftedFormJ()[item.Id] ?? []]);
        const selected: Set<number> = new Set(draftSet.has(item.Id) || (draftSet.size === 0 && submitted.has(absCurrent - 1)) ? this.absMonths().filter(m => m.id >= absCurrent && m.id > start && m.id <= end).map(m => m.id) : []);
        if (selected.has(absCurrent)) currentSelected.push(item.Id);
        // #endregion
        acc.push({formJ: item, monthlyInterest: monthlyInterest, months: [[...selected], [...submitted]], cfTerm: cfTerm, cfAmount: cfTerm * monthlyInterest, absStartMonth: start, absEndMonth: end });
      }
      return acc;
    }, [] as FormJSelection[]);
    this.currentSelected.set(currentSelected);
    this.formJSelection.set(formJSelection);
  }

  checkPrevMonthSubmitted() {
    const prev = [...this.docs().prevSubmitted, ...this.docs().prevDraft].map(d => {
      const month = this.getDocMonth(d.IssuanceDate);
      const year = d.IssuanceDate.getFullYear();
      return {month: month, year: year, absMonth: toAbsoluteMonth(year, month)}
    }).sort((a, b) => b.absMonth - a.absMonth);
    if (!this.submitted() && (prev[0].absMonth < (this.absCurrentMonth() - 1))) {
      this.sharedService.openAlertCallbackAction('Please submit or draft previous months form J before proceed! You will be redirected to target month after this pop up is closed.', ALERT_TYPE.DANGER, () => {
        const next = fromAbsoluteMonth(prev[0].absMonth + 1);
        this.setRoute(this.businessId(), this.branchId(), next.year, next.month);
      });
    }
  }

  goToCustomer(id: number) {
    this.router.navigate(['/manageCustomer', id]);
  }

  async openFormJ(formJSelection: FormJSelection) {
    const formJ = formJSelection.formJ;
    const customerId = formJ.CustomerId;
    const files = formJSelection.stampedFormJ ?? await this.formJService.getCustomerFormJFiles(customerId, formJ.Id);
    const modalRef = this.sharedService.openModal(ThumbnailsListComponent, 'largeModal');
    const i = modalRef.componentInstance as ThumbnailsListComponent;
    i.fileArr.set(files);
    i.injectContent = [new DialogItem(this.formJDetailsTemplate(), { formJ: formJSelection.formJ }, INJECT_TYPE.TEMPLATE)];
  }

  getDocMonth(aDate: Date): number {
    return aDate.getDate() > this.latestDay ? aDate.getMonth() : aDate.getMonth() - 1;
  }

  isGroupHeader(index, item): boolean {
    return item.hasOwnProperty('group');
  }

  // withinTerm(month: number, firstTerm: Date, lastTerm: Date) {
  //   const d = new Date(month > this.yearEnd() ? this.currentFY() - 1 : this.currentFY(), month, 1);
  //   // console.log(firstTerm, d, lastTerm)
  //   return firstTerm < d && d <= lastTerm;
  // }

  toggleSelectAll(group: FormJSelectionGroup, checked: boolean) {
    group.selectionList.forEach(j => this.toggleSelect(j, checked));
  }

  toggleSelect(formJSelection: FormJSelection, isChecked: boolean) {
    // if (isChecked) {
    //   formJSelection.months[Status.Selected] = [...formJSelection.months[Status.Selected], ...this.affectedMonths().filter(m => this.withinTerm(m, formJSelection.formJ.DateStamped, formJSelection.lastTerm))];
    //   this.currentSelected.update(s => [...s, formJSelection.formJ.Id]);
    // } else {
    //   formJSelection.months[Status.Selected] = formJSelection.months[Status.Selected].filter(n => !this.affectedMonths().includes(n));
    //   this.currentSelected.update(selected => selected.filter(s => s !== formJSelection.formJ.Id));
    // }
    // this.formJSelection.update(s => {
    //   const i = s.findIndex(j => j.formJ.Id === formJSelection.formJ.Id);
    //   s[i] = formJSelection;
    //   return [...s];
    // })

    const absCurrent = this.absCurrentMonth();
    if (isChecked) {
      formJSelection.months[Status.Selected] = Array.from(new Set([...formJSelection.months[Status.Selected], ...this.absMonths().filter(m => m.id >= absCurrent && m.id >= formJSelection.absStartMonth && m.id <= formJSelection.absEndMonth).map(m => m.id)]));
      this.currentSelected.update(s => [...s, formJSelection.formJ.Id]);
    } else {
      formJSelection.months[Status.Selected] = formJSelection.months[Status.Selected].filter(n => n < absCurrent);
      this.currentSelected.update(selected => selected.filter(s => s !== formJSelection.formJ.Id));
    }
    this.formJSelection.update(s => {
      const i = s.findIndex(j => j.formJ.Id === formJSelection.formJ.Id);
      s[i] = formJSelection;
      return [...s];
    });
  }

  async updateEInvoiceDoc() {
    if (this.validateIssuanceDate()) {
      if (this.docs().currentDraft.length > 0) {
        const req = this.docs().currentDraft.map(a => {
          a.IssuanceDate = this.issuanceDate();
          return this.eInvoiceService.putEInvoiceDocs(a);
        });
        // this.currentDocs.update(docs => docs.map(d => { d.IssuanceDate = this.issuanceDate(); return d }));
        // this.originalIssuanceDate.set(this.issuanceDate());
      }
    }
  }

  async postEInvoice() {
    if (this.validateIssuanceDate()) {
      const formJIds = this.formJSelection().flatMap(j => j.months[Status.Selected].includes(this.absCurrentMonth()) ? [j.formJ.Id] : []);
      this.newEInvoiceDoc = new EInvoiceDoc();
      this.newEInvoiceDoc.FormJIds = formJIds;
      this.newEInvoiceDoc.IssuanceDate = this.issuanceDate();
      const e = await this.eInvoiceService.postEInvoiceDocs(this.newEInvoiceDoc);
      if (this.docs().currentDraft.length > 0) {
        await this.eInvoiceService.deleteEInvoiceDocs(this.docs().currentDraft.map(e => e.Id));
      }
      this.sharedService.openAlert(`${this.monthsLong[this.currentMonth()]} selections saved successfully! You can generate excel file to submit e-invoice at myInvoice portal.`, ALERT_TYPE.SUCCESS);
      this.getEInvoiceDocs(this.businessId());
    }
  }

  async exportExcel() {
    if (this.validateIssuanceDate()) {
      const f = await this.eInvoiceService.postEInvoiceExcelFile(this.docs().currentDraft.map(e => e.Id));
      this.fileService.downloadFile(f);
    }
  }

  validateIssuanceDate(): boolean {
    if (this.issuanceDate() < this.minDate() || this.issuanceDate() > this.maxIssuanceDate()) {
      if (this.minDate() > this.maxIssuanceDate()) {
        return true;
      }
      this.sharedService.openAlert(ERROR_MESSAGES.NOT_IN_DATE_RANGE('Issuance Date', this.sharedService.formatDate(this.minDate()), this.sharedService.formatDate(this.maxIssuanceDate())), ALERT_TYPE.DANGER);
      return false;
    }
      return true;
  }

  uploadProof() {
    const modalRef = this.sharedService.openModal(FileUploaderComponent);
    const i = modalRef.componentInstance as FileUploaderComponent;
    i.title = 'Upload E-Invoice Submission Proof';
    i.fileAction = { type: 'EINVOICE', url: this.constructApi.getSubmitEInvoiceDocs() };
  }

  hasCancelledLoan(group: FormJSelectionGroup) {
    return group.selectionList.some(l => l.cancelled);
  }

  getSubTotal(group: FormJSelectionGroup) {
    return group.selectionList.reduce((total, l) => total + this.calcFYTotalAmount(l), 0);
  }

  getSubCurrentAmount(group: FormJSelectionGroup) {
    return group.selectionList.reduce((total, l) => total + (l.months.flat().includes(this.absCurrentMonth()) ? l.monthlyInterest : 0), 0);
  }

  getSubCF(group: FormJSelectionGroup) {
    return group.selectionList.reduce((total, l) => total + (l.months.flat().includes(this.absCurrentMonth()) ? l.cfAmount : 0), 0);
  }

  toggleCollapse(group: number) {
    this.expansionState.update(s => {
      s[group] = !s[group]
      return [...s];
    })
  }

  goToDashboard() {
    this.router.navigate(['/e-invoice-dashboard']);
  }

  reset() {
    console.log('reset');
    this.businessId.set(undefined);
    this.branchId.set(undefined);
    this.formJList.set([]);
    this.allEInvoiceDocs.set([]);
    this.formJSelection.set([]); // TODO: check if table cleared
    this.yearEnd.set(-1);
    this.limit.set(undefined);
    this.accumNextYearCF.set(undefined);
    this.prevYearBF.set(undefined);
  }
}
